import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import QRCode from 'qrcode';

const AuthenticatorSetup = ({ onComplete, onBack }) => {
  const { user, userProfile } = useAuth();
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);

  useEffect(() => {
    generateSecret();
  }, []);

  const generateSecret = () => {
    // Generate a random base32 secret (simplified version)
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let newSecret = '';
    for (let i = 0; i < 32; i++) {
      newSecret += chars?.charAt(Math.floor(Math.random() * chars?.length));
    }
    setSecret(newSecret);
    generateQRCode(newSecret);
  };

  const generateQRCode = async (secretKey) => {
    try {
      const issuer = 'TrainaryGym';
      const accountName = userProfile?.email || user?.email;
      const otpauthUrl = `otpauth://totp/${issuer}:${accountName}?secret=${secretKey}&issuer=${issuer}`;
      
      const qrUrl = await QRCode?.toDataURL(otpauthUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      
      setQrCodeUrl(qrUrl);
    } catch (err) {
      console.error('QR Code generation error:', err);
      setError('Failed to generate QR code');
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode?.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setVerifying(true);
    setError('');

    // Simulate verification (in production, this would verify against the TOTP algorithm)
    setTimeout(() => {
      // For demo purposes, accept any 6-digit code
      if (/^\d{6}$/?.test(verificationCode)) {
        onComplete(secret);
      } else {
        setError('Invalid verification code. Please try again.');
        setVerifying(false);
      }
    }, 1000);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Setup Authenticator App</h2>
          <p className="text-sm text-muted-foreground mt-1">Scan the QR code with your authenticator app</p>
        </div>
        <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors">
          <Icon name="ArrowLeft" size={20} />
        </button>
      </div>
      {/* Supported Apps */}
      <div className="bg-muted/30 rounded-lg p-4 mb-6">
        <p className="text-sm font-medium text-foreground mb-3">Supported Authenticator Apps:</p>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background">
            <Icon name="Smartphone" size={16} className="text-primary" />
            <span className="text-sm text-foreground">Google Authenticator</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background">
            <Icon name="Smartphone" size={16} className="text-primary" />
            <span className="text-sm text-foreground">Microsoft Authenticator</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background">
            <Icon name="Smartphone" size={16} className="text-primary" />
            <span className="text-sm text-foreground">Authy</span>
          </div>
        </div>
      </div>
      {/* QR Code */}
      <div className="text-center mb-6">
        {qrCodeUrl ? (
          <div className="inline-block p-4 bg-white rounded-xl shadow-sm">
            <img src={qrCodeUrl} alt="QR Code" className="w-64 h-64" />
          </div>
        ) : (
          <div className="w-64 h-64 mx-auto bg-muted/30 rounded-xl flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        )}
      </div>
      {/* Manual Entry Toggle */}
      <div className="text-center mb-6">
        <button
          onClick={() => setShowManualEntry(!showManualEntry)}
          className="text-sm text-primary hover:underline flex items-center gap-2 mx-auto"
        >
          <Icon name="Key" size={16} />
          {showManualEntry ? 'Hide' : 'Show'} manual entry code
        </button>
      </div>
      {/* Manual Entry Code */}
      {showManualEntry && (
        <div className="bg-muted/30 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-foreground mb-2">Manual Entry Code:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-background rounded text-sm font-mono text-foreground">
              {secret}
            </code>
            <button
              onClick={() => copyToClipboard(secret)}
              className="p-2 rounded hover:bg-background transition-colors"
              title="Copy to clipboard"
            >
              <Icon name="Copy" size={16} className="text-muted-foreground" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Enter this code manually if you can't scan the QR code
          </p>
        </div>
      )}
      {/* Verification */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Enter Verification Code
          </label>
          <Input
            type="text"
            placeholder="000000"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6))}
            maxLength={6}
            className="text-center text-2xl tracking-widest font-mono"
          />
          <p className="text-xs text-muted-foreground mt-2">
            Enter the 6-digit code from your authenticator app
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive">
            <Icon name="AlertCircle" size={16} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onBack}
            className="flex-1"
          >
            Back
          </Button>
          <Button
            onClick={handleVerify}
            disabled={verifying || verificationCode?.length !== 6}
            className="flex-1"
          >
            {verifying ? 'Verifying...' : 'Verify & Continue'}
          </Button>
        </div>
      </div>
      {/* Instructions */}
      <div className="mt-6 p-4 rounded-lg bg-primary/5 border border-primary/20">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-2">Setup Instructions:</p>
            <ol className="space-y-1 list-decimal list-inside">
              <li>Open your authenticator app</li>
              <li>Tap "Add Account" or "+"</li>
              <li>Scan the QR code above</li>
              <li>Enter the 6-digit code to verify</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticatorSetup;